/**
 * Mark bounds — size the MARK, not the FILE.
 *
 * Every surface that composites a dealer's mark into a platform-owned shape
 * (the brand_showcase header badge, the footer watermark, favicons, OG
 * images) historically sized the *file*. Dealer files frame their artwork
 * arbitrarily: a tight crop, generous transparent padding, or a baked-in
 * backing plate. So the same `logo_size` produced a different apparent mark
 * size per dealer, and whitespace living INSIDE the image was unreachable by
 * CSS padding — you cannot pad your way out of the file's own framing.
 *
 * `media_items.content_bounds` records where the mark actually sits inside
 * its file, as fractions of the file box (migration 337). This module turns
 * those fractions into the CSS that makes the mark fill its container, so
 * the container's size means "how big is the mark" for every dealer.
 *
 * Pure + dependency-free by design — the render side of the same contract
 * the media pipeline writes.
 */

/** The mark's bounding box within its file, as fractions (0..1) of the file box. */
export type MarkBounds = {
  x: number
  y: number
  w: number
  h: number
}

/** Runtime guard — bounds arrive as free-form `jsonb`, so never trust the shape. */
export function isMarkBounds(value: unknown): value is MarkBounds {
  if (!value || typeof value !== 'object') return false
  const b = value as Record<string, unknown>
  const nums = [b.x, b.y, b.w, b.h]
  if (!nums.every((n) => typeof n === 'number' && Number.isFinite(n))) return false
  const { x, y, w, h } = b as MarkBounds
  // Degenerate or out-of-range boxes are treated as "unknown" rather than
  // rendered — a zero-width box would divide by zero below.
  if (w <= 0 || h <= 0) return false
  if (x < 0 || y < 0 || x + w > 1.0001 || y + h > 1.0001) return false
  return true
}

/** Whole-file framing — what a null/!valid `content_bounds` means. */
export const FULL_BOUNDS: MarkBounds = { x: 0, y: 0, w: 1, h: 1 }

export function normalizeMarkBounds(value: unknown): MarkBounds {
  return isMarkBounds(value) ? value : FULL_BOUNDS
}

/**
 * True when the bounds are (near enough) the whole file — i.e. applying the
 * transform would be a no-op. Callers use this to skip the extra wrapper
 * element entirely rather than emit an identity transform.
 */
export function isFullBleed(bounds: MarkBounds, epsilon = 0.005): boolean {
  return (
    Math.abs(bounds.x) < epsilon &&
    Math.abs(bounds.y) < epsilon &&
    Math.abs(1 - bounds.w) < epsilon &&
    Math.abs(1 - bounds.h) < epsilon
  )
}

/**
 * CSS that scales + offsets an <img> inside a clipping container so the
 * MARK — not the file — fills the container.
 *
 * The container must be `overflow: hidden` and `position: relative`; the
 * image is positioned absolutely against it. The image is blown up by
 * `1/w` × `1/h` of the container and shifted so the mark's top-left lands
 * on the container's origin.
 *
 * Returns percentage-based values only, so it is resolution-independent and
 * works identically for an SVG or any raster variant.
 *
 * Note this stretches to fill BOTH axes. Callers wanting to preserve the
 * mark's aspect should size their container to the mark's aspect ratio —
 * see `markAspect`.
 */
export function markFillStyle(bounds: MarkBounds): {
  position: 'absolute'
  width: string
  height: string
  left: string
  top: string
  maxWidth: 'none'
} {
  return {
    position: 'absolute',
    width: `${(100 / bounds.w).toFixed(4)}%`,
    height: `${(100 / bounds.h).toFixed(4)}%`,
    left: `${(-(bounds.x / bounds.w) * 100).toFixed(4)}%`,
    top: `${(-(bounds.y / bounds.h) * 100).toFixed(4)}%`,
    // Tailwind preflight sets img{max-width:100%}, which would cap the
    // blown-up image and silently under-scale the mark.
    maxWidth: 'none',
  }
}

/**
 * The mark's own aspect ratio (width ÷ height) given the FILE's aspect.
 * Lets a caller size a container to the mark rather than to the file.
 */
export function markAspect(bounds: MarkBounds, fileAspect: number): number {
  return (fileAspect * bounds.w) / bounds.h
}
